import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import * as amplitude from '@amplitude/analytics-browser';
import { useNavigate, useParams } from 'react-router-dom';
import AudioPlayerSection from '../components/AudioPlayerSection';
import DeleteRecordingDialog from '../components/DeleteRecordingDialog';
import TranscriptionSection from '../components/TranscriptionSection';
import AgentConversationTimeline from '../components/AgentConversationTimeline';
import useAgentConversationDetails from '../hooks/useAgentConversationDetails';
import {
  buildAgentConversationTimelineTurns,
  buildAgentConversationTranscript,
  formatConversationDate,
  getAgentSummarySections,
  getConversationCallerLabel,
} from '../services/agentConversationService';
import { formatDuration } from '../utils';

const SummaryCard = ({ title, icon, children }) => (
  <Paper elevation={0} sx={{ p: 2.25, borderRadius: 3, border: '1px solid #e2e8f0' }}>
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
      {icon}
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
    </Stack>
    {children}
  </Paper>
);

const metadataItems = (conversation) => {
  const metadata = conversation?.metadata && typeof conversation.metadata === 'object'
    ? conversation.metadata
    : {};

  return [
    ['Զանգահարող', getConversationCallerLabel(conversation)],
    ['Ընդունող համար', metadata.dialedNumber || '—'],
    ['Ստեղծվել է', formatConversationDate(conversation?.createdAt)],
    ['Տևողություն', formatDuration(Number(conversation?.duration || 0))],
    ['Կարգավիճակ', conversation?.status || '—'],
    ['Ձևաչափ', conversation?.fileType || 'audio/ogg'],
  ];
};

const AgentConversationDetails = () => {
  const navigate = useNavigate();
  const { agentId = '', conversationId = '' } = useParams();
  const {
    agent,
    conversation,
    loading,
    error,
    audioUrl,
    sessionReport,
    canonicalAgentId,
    redirectPath,
    deleteConversation,
    updateConversation,
  } = useAgentConversationDetails(agentId, decodeURIComponent(conversationId));

  const [copyStatus, setCopyStatus] = useState('Պատճենել');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playerHeight, setPlayerHeight] = useState(118);

  useEffect(() => {
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
  }, [navigate, redirectPath]);

  useEffect(() => {
    if (canonicalAgentId && canonicalAgentId !== agentId && conversationId) {
      navigate(`/ai-agents/${canonicalAgentId}/conversations/${encodeURIComponent(conversationId)}`, { replace: true });
    }
  }, [agentId, canonicalAgentId, conversationId, navigate]);

  useEffect(() => {
    if (conversation?.title) {
      document.title = `${conversation.title} | Xosum.am`;
    } else {
      document.title = 'AI agent conversation | Xosum.am';
    }

    return () => {
      document.title = 'Xosum.am';
    };
  }, [conversation?.title]);

  const agentDisplayName = agent?.name || conversation?.metadata?.agentName || 'AI Agent';
  const callerLabel = getConversationCallerLabel(conversation);
  const timelineTurns = useMemo(() => buildAgentConversationTimelineTurns({
    turns: conversation?.agentConversation,
    sessionReport,
    agentName: agentDisplayName,
    callerLabel,
  }), [agentDisplayName, callerLabel, conversation?.agentConversation, sessionReport]);
  const transcriptText = useMemo(() => (
    timelineTurns.length > 0
      ? buildAgentConversationTranscript(timelineTurns, agentDisplayName, callerLabel)
      : conversation?.diarizedTranscription || conversation?.transcription || ''
  ), [
    agentDisplayName,
    callerLabel,
    conversation?.diarizedTranscription,
    conversation?.transcription,
    timelineTurns,
  ]);
  const summary = useMemo(() => getAgentSummarySections(conversation), [conversation]);
  const resolvedSummary = summary || {
    intent: '',
    overview: '',
    outcome: '',
    actionItems: [],
  };
  const pageBottomPadding = audioUrl ? playerHeight + 52 : 40;

  const handleCopyTranscript = (text) => {
    const nextText = text ? `${text}\n\nԶրույցի հղումը՝ ${window.location.href}` : '';
    navigator.clipboard.writeText(nextText);
    setCopyStatus('Պատճենված է');
    amplitude.track('Agent Conversation Copied', { conversationId });
    window.setTimeout(() => setCopyStatus('Պատճենել'), 2000);
  };

  const handleToggleStar = async () => {
    if (!conversation) {
      return;
    }

    await updateConversation({ starred: !conversation.starred });
  };

  const handleDelete = async () => {
    await deleteConversation();
    navigate(`/ai-agents/${canonicalAgentId || agentId}`);
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 4, border: '1px solid #fecaca', bgcolor: '#fff5f5' }}>
          <Typography variant="h6" color="error">{error}</Typography>
        </Paper>
      </Container>
    );
  }

  if (!conversation) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 4, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6">Զրույցը չի գտնվել։</Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f4f7fb 0%, #ffffff 48%)',
        pb: `${pageBottomPadding}px`,
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
        <Stack spacing={2.5}>
          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, border: '1px solid #e2e8f0' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <IconButton
                    onClick={() => navigate(`/ai-agents/${canonicalAgentId || agentId}`)}
                    sx={{
                      bgcolor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      '&:hover': { bgcolor: '#eff6ff' },
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {conversation.title || callerLabel}
                    </Typography>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {agentDisplayName || 'AI ագենտ'} · {callerLabel}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Tooltip title={conversation.starred ? 'Հեռացնել աստղը' : 'Ավելացնել աստղ'}>
                    <IconButton
                      onClick={handleToggleStar}
                      sx={{
                        width: 46,
                        height: 46,
                        border: '1px solid #e2e8f0',
                        color: conversation.starred ? 'warning.main' : 'text.secondary',
                      }}
                    >
                      {conversation.starred ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                  </Tooltip>
                  <Button
                    color="error"
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Ջնջել
                  </Button>
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
                <Chip icon={<CallOutlinedIcon />} label={callerLabel} />
                <Chip icon={<AccessTimeOutlinedIcon />} label={formatConversationDate(conversation.createdAt)} />
                <Chip icon={<NotesOutlinedIcon />} label={formatDuration(Number(conversation.duration || 0))} />
                {conversation?.metadata?.dialedNumber && (
                  <Chip icon={<PhoneForwardedOutlinedIcon />} label={conversation.metadata.dialedNumber} />
                )}
              </Stack>
            </Stack>
          </Paper>

          {(conversation.resolution || resolvedSummary.intent || resolvedSummary.overview || resolvedSummary.outcome || resolvedSummary.actionItems.length > 0) && (
            <Stack spacing={2}>
              {conversation.resolution && (
                <SummaryCard title="Կարճ ամփոփում" icon={<AssignmentOutlinedIcon color="primary" />}>
                  <Typography sx={{ lineHeight: 1.8 }}>
                    {conversation.resolution}
                  </Typography>
                </SummaryCard>
              )}

              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
                {(resolvedSummary.intent || resolvedSummary.overview || resolvedSummary.outcome) && (
                  <SummaryCard title="Զանգի եզրակացություններ" icon={<NotesOutlinedIcon color="primary" />}>
                    <Stack spacing={1.25}>
                      {resolvedSummary.intent && (
                        <Typography sx={{ lineHeight: 1.75 }}>
                          <strong>Նպատակ:</strong> {resolvedSummary.intent}
                        </Typography>
                      )}
                      {resolvedSummary.overview && (
                        <Typography sx={{ lineHeight: 1.75 }}>
                          <strong>Ընթացք:</strong> {resolvedSummary.overview}
                        </Typography>
                      )}
                      {resolvedSummary.outcome && (
                        <Typography sx={{ lineHeight: 1.75 }}>
                          <strong>Արդյունք:</strong> {resolvedSummary.outcome}
                        </Typography>
                      )}
                    </Stack>
                  </SummaryCard>
                )}

                {resolvedSummary.actionItems.length > 0 && (
                  <SummaryCard title="Հաջորդ քայլեր" icon={<ChecklistOutlinedIcon color="primary" />}>
                    <Stack spacing={1}>
                      {resolvedSummary.actionItems.map((item) => (
                        <Typography key={item} sx={{ lineHeight: 1.75 }}>
                          • {item}
                        </Typography>
                      ))}
                    </Stack>
                  </SummaryCard>
                )}
              </Stack>
            </Stack>
          )}

          {timelineTurns.length > 0 ? (
            <AgentConversationTimeline
              turns={timelineTurns}
              copyStatus={copyStatus}
              onCopy={() => handleCopyTranscript(transcriptText)}
            />
          ) : (
            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, border: '1px solid #e2e8f0' }}>
              {transcriptText ? (
                <TranscriptionSection
                  title="Երկխոսության տրանսկրիպտ"
                  transcription={transcriptText}
                  copyStatus={copyStatus}
                  onCopy={handleCopyTranscript}
                />
              ) : (
                <Stack spacing={1}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Երկխոսության տրանսկրիպտ
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.75 }}>
                    Այս զանգի համար երկխոսության տրանսկրիպտ դեռ հասանելի չէ։
                  </Typography>
                </Stack>
              )}
            </Paper>
          )}

          <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, border: '1px solid #e2e8f0' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Զանգի տվյալներ
            </Typography>
            <Stack divider={<Divider flexItem />} spacing={0}>
              {metadataItems(conversation).map(([label, value]) => (
                <Stack
                  key={label}
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                  sx={{ py: 1.5 }}
                >
                  <Typography color="text.secondary">{label}</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Container>

      <DeleteRecordingDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
      />

      {audioUrl && (
        <AudioPlayerSection
          audioUrl={audioUrl}
          onDownloadAudio={() => amplitude.track('Agent Conversation Audio Downloaded', { conversationId })}
          onHeightChange={setPlayerHeight}
          recordingMetadata={conversation}
          showSpeakerDiarization={false}
        />
      )}
    </Box>
  );
};

export default AgentConversationDetails;
